import React, { FC, useEffect, useState, Suspense } from "react";
import { navigate } from "gatsby";
import { DealCardClickArea, DealCardsWrapper, DealListPage } from "./styled";
import EmptyState from "@components/EmptyState/EmptyState.component";
import useAnalytics from "@helpers/useAnalytics";
import { useDispatch, useSelector } from "react-redux";
import { verifyInvestorStatusSelector } from "../../state/investor/investor.selectors";
import { InlineLoader } from "@components/Loading/InlineLoader";
import { commitmentsSelector } from "@state/commitment/commitment.selectors";
import renderDealLabels from "../DealCard/renderDealLabels";
import { StringParam, useQueryParam } from "use-query-params";
import type { Deal } from "@ifgengineering/client-invest-sdk";
import { neverLoadedDealSelector } from "@state/deal/deal.selectors";
import DealCard from "../DealCard/DealCard.component";
import DealCardV2 from "../DealCard/DealCardV2.component";
import DealCardPrelaunch from "@components/DealCard/DealCardPrelaunch.component";
import { DealListLoader } from "./DealListLoader";
import { isEmailVerifiedSelector } from "@state/auth/auth.selectors";
import { isNewDealCardEnabledSelector } from "@state/remoteConfiguration/remoteConfiguration.selectors";
import {
  dealUsesFundraisingRounds,
  fetchDealsFundraisingCapacityThunk,
} from "@state/dealCapacity";
import { filterLabelsForLiveDeals } from "./helpers";

const VerifyInvestorProfileModal = React.lazy(
  () => import("./verifyInvestorProfile/VerifyInvestorProfileModal")
);

const VerifyAccountModal = React.lazy(
  () => import("./verifyAccount/VerifyAccountModal")
);

const ReferrerMap: Record<string, string> = {
  liveDeals: "Live Deals",
  ifisa: "IF-ISA Page",
};

interface DealListProps {
  deals: Array<Deal>;
  referrer?: string;
  paddingBottom?: string;
}

const DealList: FC<DealListProps> = ({
  deals,
  referrer = "liveDeals",
  paddingBottom = "24px",
}) => {
  const dispatch = useDispatch();
  const [openVerifyModal] = useQueryParam("open-verify-modal", StringParam);
  const showVerifyHighNetWorthModal = useSelector(verifyInvestorStatusSelector);
  const isEmailVerified = useSelector(isEmailVerifiedSelector);
  const showVerifyAccountModal = isEmailVerified === false;
  const commitments = useSelector(commitmentsSelector);
  const neverLoadedDeals = useSelector(neverLoadedDealSelector);
  const hasLoadedAllDeals = !neverLoadedDeals;
  const isV2Cards = useSelector(isNewDealCardEnabledSelector);
  const isDealsEmpty = !deals.length;
  const firePageViewAnalytics = useAnalytics("pageView");
  const firePageView = async (eventName: string) => {
    (await firePageViewAnalytics)({ eventName });
  };

  useEffect(() => {
    firePageView("cur8:liveDeals_pageViewed");
  }, []);

  useEffect(() => {
    if (openVerifyModal) {
      setRenderVerifyProfileModal(true);
    }
  }, [openVerifyModal]);

  useEffect(() => {
    const roundsDealIds = deals
      .filter(dealUsesFundraisingRounds)
      .map((deal) => deal.id);
    dispatch(fetchDealsFundraisingCapacityThunk(roundsDealIds));
  }, [deals]);

  const renderEmptyDeals = () =>
    neverLoadedDeals ? null : (
      <EmptyState
        heading="We are working hard to find your next deal"
        subHeading="We recommend you analyse your portfolio to see if you're diversified enough."
        rightButtonText="View Your Investments"
        onClickRight={() => navigate("/app/portfolio")}
      />
    );

  const [renderVerifyProfileModal, setRenderVerifyProfileModal] =
    useState<boolean>(false);

  const [renderVerifyAccountModal, setRenderVerifyAccountModal] =
    useState<boolean>(false);

  const handleCardClick = ({ deal }: { deal: Deal }) => {
    if (showVerifyAccountModal) {
      setRenderVerifyAccountModal(true);
      return;
    }

    if (showVerifyHighNetWorthModal) {
      setRenderVerifyProfileModal(true);
      return;
    }

    navigate(
      `/app/deals/${deal.id}?referrer=${ReferrerMap[referrer] || referrer}`
    );
  };

  return (
    <DealListPage paddingBottom={paddingBottom}>
      {showVerifyHighNetWorthModal && (
        <Suspense fallback={<InlineLoader />}>
          <VerifyInvestorProfileModal
            show={renderVerifyProfileModal}
            onClose={() => setRenderVerifyProfileModal(false)}
          />
        </Suspense>
      )}

      <Suspense fallback={<InlineLoader />}>
        <VerifyAccountModal
          show={renderVerifyAccountModal}
          onClose={() => {
            setRenderVerifyAccountModal(false);
          }}
        />
      </Suspense>

      {isDealsEmpty && renderEmptyDeals()}

      <DealCardsWrapper>
        {!hasLoadedAllDeals && <DealListLoader length={3} />}

        {hasLoadedAllDeals &&
          deals.map((deal, index) => {
            // Filter labels for live deals to show only standard labels
            const filteredLabels = filterLabelsForLiveDeals(deal.labels);
            const dealLabels = renderDealLabels(filteredLabels);
            const isPreLaunch = deal.status === "PRELAUNCH";

            return (
              <DealCardClickArea
                data-testid="deal-card-click-area"
                onClick={() => handleCardClick({ deal })}
                key={index}
              >
                {isV2Cards ? (
                  <DealCardV2
                    deal={deal}
                    filteredLabels={filteredLabels}
                    isPreLaunch={isPreLaunch}
                  />
                ) : isPreLaunch ? (
                  <DealCardPrelaunch deal={deal} dealLabels={dealLabels} />
                ) : (
                  <DealCard
                    deal={deal}
                    commitments={commitments}
                    dealLabels={dealLabels}
                  />
                )}
              </DealCardClickArea>
            );
          })}
      </DealCardsWrapper>
    </DealListPage>
  );
};

export default DealList;
